import React, { useState, useEffect } from 'react';
import './LambdaStatus.css';
import { API_CONFIG } from '../../config/api';

interface LambdaStatusEntry {
  name: string;
  status: 'Active' | 'Inactive' | 'Error';
  lastRun?: string;
  nextRun?: string;
  schedule: string;
  articlesFound?: number;
}

const LambdaStatus: React.FC = () => {
  const [lambdas, setLambdas] = useState<LambdaStatusEntry[]>([
    {
      name: 'Daily News Scraper',
      status: 'Active',
      schedule: 'Daily at 3:00 AM PST',
      lastRun: 'Loading...',
      nextRun: 'Loading...',
      articlesFound: 0
    },
    {
      name: 'Weekly Summary Generator', 
      status: 'Active',
      schedule: 'Sunday at 5:00 PM PST',
      lastRun: 'Loading...',
      nextRun: 'Loading...'
    },
    {
      name: 'AI Solutions Discovery',
      status: 'Active', 
      schedule: 'Sunday at 6:00 PM PST',
      lastRun: 'Loading...',
      nextRun: 'Loading...'
    }
  ]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLambdaStatus();
  }, []);

  const fetchLambdaStatus = async () => {
    try {
      // Check recent articles to see last scraper run
      const articlesResponse = await fetch(`${API_CONFIG.BASE_URL}/api/articles?limit=5`);
      
      if (!articlesResponse.ok) {
        throw new Error(`HTTP ${articlesResponse.status}`);
      }
      
      const articlesData = await articlesResponse.json();
      
      const lastScrapedAt = articlesData.articles?.[0]?.scraped_at;
      const lastRun = lastScrapedAt ? new Date(lastScrapedAt).toLocaleString() : 'No recent runs';
      
      // Calculate next run (3 AM PST daily)
      const now = new Date();
      const nextRun = new Date();
      nextRun.setHours(11, 0, 0, 0); // 3 AM PST = 11 AM UTC
      if (nextRun <= now) {
        nextRun.setDate(nextRun.getDate() + 1);
      }

      // Calculate next weekly runs
      const nextSunday = new Date();
      nextSunday.setDate(nextSunday.getDate() + (7 - nextSunday.getDay()) % 7);
      nextSunday.setHours(1, 0, 0, 0); // 5 PM PST = 1 AM UTC Monday
      
      const nextAISunday = new Date(nextSunday);
      nextAISunday.setHours(2, 0, 0, 0); // 6 PM PST = 2 AM UTC Monday

      setLambdas(prev => prev.map(lambda => {
        if (lambda.name === 'Daily News Scraper') {
          return { ...lambda, lastRun, nextRun: nextRun.toLocaleString(), articlesFound: articlesData.articles?.length || 0 };
        } else if (lambda.name === 'Weekly Summary Generator') {
          return { ...lambda, lastRun: 'Check weekly summaries', nextRun: nextSunday.toLocaleString() };
        } else if (lambda.name === 'AI Solutions Discovery') {
          return { ...lambda, lastRun: 'Check AI solutions', nextRun: nextAISunday.toLocaleString() };
        }
        return lambda;
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Lambda status:', error);
      
      // Set fallback data when API fails
      const now = new Date();
      const nextDaily = new Date();
      nextDaily.setHours(11, 0, 0, 0);
      if (nextDaily <= now) nextDaily.setDate(nextDaily.getDate() + 1);
      
      const nextSunday = new Date();
      nextSunday.setDate(nextSunday.getDate() + (7 - nextSunday.getDay()) % 7);
      nextSunday.setHours(1, 0, 0, 0);
      
      const nextAISunday = new Date(nextSunday);
      nextAISunday.setHours(2, 0, 0, 0);
      
      setLambdas(prev => prev.map(lambda => {
        if (lambda.name === 'Daily News Scraper') {
          return { ...lambda, lastRun: 'API Error - Check logs', nextRun: nextDaily.toLocaleString(), articlesFound: 0 };
        } else if (lambda.name === 'Weekly Summary Generator') {
          return { ...lambda, lastRun: 'Scheduled weekly', nextRun: nextSunday.toLocaleString() };
        } else if (lambda.name === 'AI Solutions Discovery') {
          return { ...lambda, lastRun: 'Scheduled weekly', nextRun: nextAISunday.toLocaleString() };
        }
        return lambda;
      }));
      
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#28a745';
      case 'Error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="lambda-status-container">
      <h2>Automated Systems Status</h2>
      <p className="status-description">
        These Lambda functions run automatically on schedule. The daily scraper was fixed to resolve database schema issues.
      </p>
      
      {loading ? (
        <div className="loading">Loading Lambda status...</div>
      ) : (
        <div className="lambda-grid">
          {lambdas.map((lambda, index) => (
            <div key={index} className="lambda-card">
              <div className="lambda-header">
                <h3>{lambda.name}</h3>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(lambda.status) }}
                >
                  {lambda.status}
                </span>
              </div>
              
              <div className="lambda-details">
                <div className="detail-row">
                  <span className="label">Schedule:</span>
                  <span className="value">{lambda.schedule}</span>
                </div>
                
                <div className="detail-row">
                  <span className="label">Last Run:</span>
                  <span className="value">{lambda.lastRun}</span>
                </div>
                
                <div className="detail-row">
                  <span className="label">Next Run:</span>
                  <span className="value">{lambda.nextRun}</span>
                </div>
                
                {lambda.articlesFound !== undefined && (
                  <div className="detail-row">
                    <span className="label">Recent Articles:</span>
                    <span className="value">{lambda.articlesFound}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="status-note">
        <h4>Recent Fix Applied:</h4>
        <p>✅ Removed 'category' field from daily scraper to fix database schema mismatch</p>
        <p>✅ Lambda should now save articles successfully every day at 3 AM PST</p>
      </div>
    </div>
  );
};

export default LambdaStatus;
